/**
 * Sound bites, synthesized at runtime with Web Audio: oscillators, envelopes, filtered noise. Each cue is a
 * small data recipe so it can be tested and tuned. Recorded versions of every cue (ElevenLabs Sound Effects,
 * apps/web/public/sfx) play instead once loaded (sound.ts); these recipes are the fallback, and their
 * `scope`, `level` and `duck` apply to the recordings too.
 *
 * The palette is a "clay toy": round triangle/sine tones with a quick downward pitch squish at the onset
 * (a boop), soft woodblock ticks, and short filtered-noise swishes, all in C major so cues that overlap
 * never clash. Cues are mixed to a similar loudness (`level`) and run through one gentle compressor.
 */

/** Every cue the platform and games can play. The last group are older names kept for games. */
export type SoundCue =
  | 'tap' | 'select' | 'join' | 'leave' | 'ready' | 'unready' | 'allReady' | 'countdown' | 'go'
  | 'submit' | 'tick' | 'tickFinal' | 'reveal' | 'correct' | 'wrong' | 'roundWin' | 'roundLose'
  | 'gameWin' | 'gameLose' | 'rematch' | 'invite' | 'vote' | 'shutter' | 'error'
  | 'warn' | 'warnFinal' | 'champion';

/** Older names that now play one of the canonical cues. */
const ALIASES = { warn: 'tick', warnFinal: 'tickFinal', champion: 'gameWin' } as const;
export type CanonicalCue = Exclude<SoundCue, keyof typeof ALIASES>;

export function resolveCue(cue: SoundCue): CanonicalCue {
  return (ALIASES as Record<string, CanonicalCue>)[cue] ?? (cue as CanonicalCue);
}

/**
 * Who hears a cue when a shared TV is in the room. `personal` cues (your tap, your lock-in, your result)
 * play on your phone; `room` cues (someone joined, everyone's ready, the reveal) play once, on the TV.
 */
export type SoundScope = 'personal' | 'room';

export type Voice =
  | {
      kind: 'tone';
      /** Start frequency (Hz) and start time (s from the cue's start). */
      f: number;
      t: number;
      /** Duration (s), including the release. */
      d: number;
      wave?: OscillatorType;
      /** Peak gain before the cue's level. */
      g?: number;
      /** Glide to this frequency over `glide` seconds (default: the whole note). */
      to?: number;
      glide?: number;
      /** The clay "squish": start this many times higher and drop to `f` in 30 ms. */
      squish?: number;
      /** Attack (s). */
      a?: number;
      /** Vibrato depth as a fraction of the frequency (6 Hz). */
      vib?: number;
      /** Lowpass cutoff (Hz). */
      lp?: number;
    }
  | {
      kind: 'noise';
      t: number;
      d: number;
      g?: number;
      /** Bandpass centre (Hz), optionally swept to `to`. Without it the noise is highpassed at `hp`. */
      band?: number;
      to?: number;
      q?: number;
      hp?: number;
    };

export interface Recipe {
  voices: Voice[];
  scope: SoundScope;
  /** Overall loudness trim, so short and long cues land at a similar perceived level. */
  level: number;
  /** How far TV music dips under this cue (0 = not at all, 1 = silent). */
  duck: number;
}

// Notes (Hz), C major / pentatonic.
const N = {
  G3: 196, Ab3: 207.65, A3: 220, Bb3: 233.08, C4: 261.63, E4: 329.63, F4: 349.23, G4: 392, A4: 440,
  C5: 523.25, D5: 587.33, E5: 659.25, G5: 783.99, A5: 880, C6: 1046.5, D6: 1174.66, E6: 1318.51, G6: 1567.98,
} as const;

const boop = (f: number, t: number, d: number, g = 0.3, wave: OscillatorType = 'triangle'): Voice => ({ kind: 'tone', f, t, d, g, wave, squish: 1.35, a: 0.006 });
const bell = (f: number, t: number, d: number, g = 0.22): Voice[] => [
  { kind: 'tone', f, t, d, g, wave: 'sine', a: 0.004 },
  { kind: 'tone', f: f * 2.01, t, d: d * 0.5, g: g * 0.25, wave: 'sine', a: 0.004 },
];

export const RECIPES: Record<CanonicalCue, Recipe> = {
  tap: { scope: 'personal', level: 0.7, duck: 0, voices: [boop(N.A5, 0, 0.06, 0.28, 'sine')] },
  select: { scope: 'personal', level: 0.75, duck: 0, voices: [boop(N.E5, 0, 0.06, 0.26), boop(N.A5, 0.05, 0.08, 0.26)] },
  join: { scope: 'room', level: 0.9, duck: 0.3, voices: [boop(N.G5, 0, 0.12), boop(N.C6, 0.09, 0.2), ...bell(N.E6, 0.16, 0.18, 0.08)] },
  leave: { scope: 'room', level: 0.75, duck: 0.2, voices: [boop(N.C6, 0, 0.11, 0.24), boop(N.G5, 0.09, 0.18, 0.22)] },
  ready: { scope: 'personal', level: 0.85, duck: 0.2, voices: [...bell(N.C6, 0, 0.28, 0.24), boop(N.G5, 0, 0.08, 0.14)] },
  unready: { scope: 'personal', level: 0.75, duck: 0, voices: [{ kind: 'tone', f: N.E5, t: 0, d: 0.16, g: 0.26, wave: 'triangle', to: N.C5, a: 0.005, lp: 2200 }] },
  allReady: { scope: 'room', level: 0.9, duck: 0.4, voices: [N.C5, N.E5, N.G5, N.C6].map((f, i) => boop(f, i * 0.07, i === 3 ? 0.3 : 0.12, 0.26)) },
  countdown: { scope: 'room', level: 0.85, duck: 0.4, voices: [{ kind: 'tone', f: N.A5, t: 0, d: 0.11, g: 0.26, wave: 'triangle', a: 0.004 }, { kind: 'noise', t: 0, d: 0.03, g: 0.08, band: 3000, q: 2 }] },
  go: {
    scope: 'room', level: 0.95, duck: 0.5,
    voices: [
      ...[N.C5, N.E5, N.G5, N.C6].map((f): Voice => ({ kind: 'tone', f: f * 0.75, to: f, glide: 0.06, t: 0, d: 0.45, g: 0.16, wave: 'triangle', a: 0.01 })),
      { kind: 'noise', t: 0, d: 0.25, g: 0.1, band: 900, to: 5000, q: 1.2 },
    ],
  },
  submit: {
    scope: 'personal', level: 0.85, duck: 0,
    voices: [{ kind: 'noise', t: 0, d: 0.035, g: 0.22, band: 1400, q: 3 }, boop(N.G5, 0.015, 0.09, 0.24), boop(N.C6, 0.075, 0.14, 0.24)],
  },
  tick: { scope: 'room', level: 0.7, duck: 0, voices: [{ kind: 'tone', f: 1250, t: 0, d: 0.05, g: 0.3, wave: 'sine', a: 0.002 }, { kind: 'noise', t: 0, d: 0.02, g: 0.1, band: 2600, q: 4 }] },
  tickFinal: { scope: 'room', level: 0.85, duck: 0.2, voices: [{ kind: 'tone', f: 1660, t: 0, d: 0.14, g: 0.32, wave: 'sine', a: 0.002 }, { kind: 'noise', t: 0, d: 0.03, g: 0.12, band: 3200, q: 4 }] },
  reveal: {
    scope: 'room', level: 0.9, duck: 0.5,
    voices: [
      { kind: 'noise', t: 0, d: 0.34, g: 0.12, band: 400, to: 3500, q: 1.5 },
      { kind: 'tone', f: 300, to: 900, t: 0, d: 0.32, g: 0.1, wave: 'triangle', a: 0.05 },
      ...bell(N.C6, 0.3, 0.26, 0.24),
      ...bell(N.G6, 0.3, 0.2, 0.06),
    ],
  },
  correct: { scope: 'personal', level: 0.9, duck: 0.3, voices: [boop(N.E5, 0, 0.12, 0.26), boop(N.C6, 0.09, 0.24, 0.28), ...bell(N.G6, 0.12, 0.16, 0.05)] },
  wrong: { scope: 'personal', level: 0.8, duck: 0.2, voices: [{ kind: 'tone', f: N.A3 * 1.1, to: 165, t: 0, d: 0.3, g: 0.26, wave: 'square', lp: 900, a: 0.006 }, { kind: 'tone', f: N.A3, to: 160, t: 0, d: 0.3, g: 0.14, wave: 'triangle', a: 0.006 }] },
  roundWin: { scope: 'personal', level: 0.9, duck: 0.45, voices: [...[N.C5, N.E5, N.G5].map((f, i) => boop(f, i * 0.07, 0.12, 0.24)), boop(N.C6, 0.21, 0.32, 0.28), ...bell(N.E6, 0.21, 0.26, 0.06)] },
  roundLose: { scope: 'personal', level: 0.75, duck: 0.2, voices: [boop(N.G4, 0, 0.16, 0.26), { kind: 'tone', f: N.E4, t: 0.14, d: 0.3, g: 0.26, wave: 'triangle', to: N.E4 * 0.97, a: 0.008, lp: 1800 }] },
  gameWin: {
    scope: 'personal', level: 0.95, duck: 0.65,
    voices: [
      ...[N.G4, N.C5, N.E5, N.G5].map((f, i) => boop(f, i * 0.1, 0.14, 0.24, 'square')).map((v) => ({ ...v, lp: 2600 })),
      ...[N.C5, N.E5, N.G5, N.C6].map((f): Voice => ({ kind: 'tone', f, t: 0.42, d: 0.7, g: 0.13, wave: 'triangle', a: 0.02, vib: 0.006 })),
      ...bell(N.E6, 0.42, 0.4, 0.08),
      { kind: 'noise', t: 0.42, d: 0.5, g: 0.06, band: 6000, q: 0.8 },
    ],
  },
  gameLose: {
    // A playful "wah wah wah wahhh": four falling semitones on a muted, lowpassed horn.
    scope: 'personal', level: 0.8, duck: 0.5,
    voices: [N.Bb3, N.A3, N.Ab3].map((f, i): Voice => ({ kind: 'tone', f, t: i * 0.24, d: 0.22, g: 0.3, wave: 'sawtooth', lp: 900, a: 0.03, to: f * 0.985 }))
      .concat([{ kind: 'tone', f: N.G3, t: 0.72, d: 0.55, g: 0.3, wave: 'sawtooth', lp: 800, a: 0.03, vib: 0.018 }]),
  },
  rematch: { scope: 'room', level: 0.9, duck: 0.35, voices: [{ kind: 'tone', f: N.G4, to: N.D5, glide: 0.08, t: 0, d: 0.16, g: 0.26, wave: 'square', lp: 2400, a: 0.005 }, boop(N.G5, 0.12, 0.2, 0.26), { kind: 'noise', t: 0.12, d: 0.05, g: 0.14, band: 1800, q: 1.5 }] },
  invite: { scope: 'personal', level: 0.9, duck: 0.35, voices: [...bell(N.E5, 0, 0.3, 0.26), ...bell(N.C5, 0.22, 0.36, 0.26)] },
  vote: { scope: 'personal', level: 0.75, duck: 0, voices: [boop(N.D6, 0, 0.07, 0.22), boop(N.G6 * 0.75, 0.05, 0.1, 0.18)] },
  shutter: { scope: 'personal', level: 0.8, duck: 0, voices: [{ kind: 'noise', t: 0, d: 0.06, g: 0.3, hp: 2000 }, { kind: 'noise', t: 0.07, d: 0.05, g: 0.2, hp: 3000 }] },
  error: { scope: 'personal', level: 0.75, duck: 0, voices: [{ kind: 'tone', f: 233, t: 0, d: 0.12, g: 0.26, wave: 'triangle', lp: 1500, a: 0.004 }, { kind: 'tone', f: 196, t: 0.12, d: 0.18, g: 0.26, wave: 'triangle', lp: 1500, a: 0.004 }] },
};

/** Seconds from the cue's start until its last voice ends. */
export function recipeDuration(r: Recipe): number {
  return r.voices.reduce((m, v) => Math.max(m, v.t + v.d), 0);
}

/**
 * One white-noise second per context, reused by every noise voice. Fixed seed: the same "random" hiss
 * each time (no Math.random needed, and the cue sounds identical on every device).
 */
const noiseBuffers = new WeakMap<BaseAudioContext, AudioBuffer>();
export function noiseBuffer(ctx: BaseAudioContext): AudioBuffer {
  let b = noiseBuffers.get(ctx);
  if (!b) {
    b = ctx.createBuffer(1, Math.floor(ctx.sampleRate), ctx.sampleRate);
    const data = b.getChannelData(0);
    let s = 0x2f6b9d1;
    for (let i = 0; i < data.length; i++) {
      s ^= s << 13; s ^= s >>> 17; s ^= s << 5;
      data[i] = ((s >>> 0) / 0xffffffff) * 2 - 1;
    }
    noiseBuffers.set(ctx, b);
  }
  return b;
}

const SILENT = 0.0001;

/** Exponential attack/decay envelope on a gain param (exponential ramps can't touch 0). */
export function envelope(p: AudioParam, at: number, peak: number, attack: number, dur: number) {
  const a = Math.max(0.002, Math.min(attack, dur * 0.5));
  p.setValueAtTime(SILENT, at);
  p.exponentialRampToValueAtTime(Math.max(SILENT * 2, peak), at + a);
  p.exponentialRampToValueAtTime(SILENT, at + dur);
}

/** Schedules one recipe at `at` (context time) into `dest`, scaled by `volume`. Returns nodes started. */
export function renderRecipe(ctx: BaseAudioContext, dest: AudioNode, r: Recipe, at: number, volume: number): number {
  let started = 0;
  const level = r.level * volume;
  for (const v of r.voices) {
    const t = at + v.t;
    const g = ctx.createGain();
    envelope(g.gain, t, (v.g ?? 0.25) * level, v.kind === 'tone' ? v.a ?? 0.008 : 0.003, v.d);
    if (v.kind === 'tone') {
      const osc = ctx.createOscillator();
      osc.type = v.wave ?? 'sine';
      const fq = osc.frequency;
      if (v.squish) {
        fq.setValueAtTime(v.f * v.squish, t);
        fq.exponentialRampToValueAtTime(v.f, t + 0.03);
      } else fq.setValueAtTime(v.f, t);
      if (v.to) fq.exponentialRampToValueAtTime(v.to, t + (v.glide ?? v.d));
      let out: AudioNode = osc;
      if (v.lp) {
        const f = ctx.createBiquadFilter();
        f.type = 'lowpass';
        f.frequency.setValueAtTime(v.lp, t);
        out = out.connect(f);
      }
      out.connect(g).connect(dest);
      if (v.vib) {
        const lfo = ctx.createOscillator();
        const depth = ctx.createGain();
        lfo.frequency.setValueAtTime(6, t);
        depth.gain.setValueAtTime(v.f * v.vib, t);
        lfo.connect(depth).connect(fq);
        lfo.start(t);
        lfo.stop(t + v.d + 0.02);
      }
      osc.start(t);
      osc.stop(t + v.d + 0.02);
    } else {
      const src = ctx.createBufferSource();
      src.buffer = noiseBuffer(ctx);
      const f = ctx.createBiquadFilter();
      if (v.band) {
        f.type = 'bandpass';
        f.Q.setValueAtTime(v.q ?? 1, t);
        f.frequency.setValueAtTime(v.band, t);
        if (v.to) f.frequency.exponentialRampToValueAtTime(v.to, t + v.d);
      } else {
        f.type = 'highpass';
        f.frequency.setValueAtTime(v.hp ?? 1000, t);
      }
      src.connect(f).connect(g).connect(dest);
      // Start somewhere different in the second of noise so repeats don't sound stamped.
      src.start(t, (started * 0.137) % 0.5);
      src.stop(t + v.d + 0.02);
    }
    started++;
  }
  return started;
}
